// The global Claude (decision 31.10.2026): chats on the left, the conversation with its documents on the right, the
// question box below. The worker writes the answer while the page polls every second; the box keeps its text, only
// list and conversation are redrawn. Claude only reads the pipeline; documents can be kept at a deal.
import type { ClaudeChat } from "../api";
import { markdown } from "../markdown";
import { render, S } from "../state";
import { $, esc, fmtDT, toast } from "../util";
import { openPipelineLink } from "./links";

const POLL_MS = 1000;
const EXAMPLES = [
  "Welche Deals warten seit mehr als zwei Wochen auf uns?",
  "Erstell mir eine Übersicht aller offenen Deals mit Volumen als Tabelle.",
  "Bereite mich auf das nächste Gespräch mit einem Kunden vor: Was ist der Stand, was ist offen?",
  "Welche Anfragen der Stufe A sind noch offen, und was fehlt jeweils?",
];

let polling: ReturnType<typeof setTimeout> | undefined;
let sending = false;

const running = (c: ClaudeChat | null) => !!c?.messages.some((m) => m.status === "running");

export async function loadClaude(): Promise<void> {
  const id = S.claudeChatId;
  const [chats, chat] = await Promise.all([S.api.claudeChats(), id ? S.api.claudeChat(id).catch(() => null) : Promise.resolve(null)]);
  S.claudeChats = chats;
  // The user may have switched chats while this loaded: only the chat still open is shown.
  if (S.claudeChatId !== id) return;
  if (id && !chat) {
    // Someone else's chat, or gone: back to a new chat instead of a page that cannot send.
    S.claudeChatId = null;
    history.replaceState(null, "", "#claude");
    toast("Diesen Chat gibt es nicht (mehr).");
  }
  S.claudeChat = chat;
  schedule();
}

/** While an answer is being written, the chat is fetched again every second. */
function schedule() {
  clearTimeout(polling);
  if (!running(S.claudeChat) || S.view !== "claude") return;
  const id = S.claudeChat!.id;
  polling = setTimeout(async () => {
    if (S.view !== "claude" || S.claudeChatId !== id) return;
    try {
      const chat = await S.api.claudeChat(id);
      // A late answer for a chat the user has left must not replace the open one.
      if (S.view !== "claude" || S.claudeChatId !== id) return;
      S.claudeChat = chat;
      if (!running(chat)) S.claudeChats = await S.api.claudeChats();
    } catch {
      // The next poll tries again.
    }
    renderClaude();
    schedule();
  }, POLL_MS);
}

function chatList(): string {
  const chats = S.claudeChats;
  if (!chats) return `<p class="muted">Lade …</p>`;
  if (!chats.length) return `<p class="muted cl-none">Noch keine Chats.</p>`;
  return `<ul>${chats
    .map(
      (c) =>
        `<li><a href="#claude-${esc(c.id)}" class="${c.id === S.claudeChatId ? "on" : ""}"><span>${esc(c.title)}</span><small class="mono">${c.running ? "antwortet …" : esc(fmtDT(c.updatedAt))}</small></a></li>`,
    )
    .join("")}</ul>`;
}

function conversation(): string {
  const c = S.claudeChat;
  if (!S.claudeChatId || !c) {
    return `<div class="cl-intro"><h2>Frag Claude</h2>
      <p>Claude schlägt selbst in der Pipeline nach: Deals, Mails, Anhänge, Notizen, Anfragen, Firmen und Briefings. Er beantwortet Fragen über mehrere Deals, erstellt Übersichten und Dokumente und nennt seine Quellen. Er kann nur lesen, nichts senden oder ändern.</p>
      <div class="cl-examples">${EXAMPLES.map((e) => `<button type="button" class="chip" data-example="${esc(e)}">${esc(e)}</button>`).join("")}</div></div>`;
  }
  const msgs = c.messages
    .map((m) => {
      if (m.role === "user") return `<div class="cl-q">${m.web ? `<span class="pill p-info">mit Websuche</span> ` : ""}${esc(m.text).replace(/\n/g, "<br>")}</div>`;
      const steps = m.steps.length
        ? `<details class="cl-steps" ${m.status === "running" ? "open" : ""}><summary>${m.status === "running" ? "Arbeitet" : "Nachgeschlagen"}: ${m.steps.length} ${m.steps.length === 1 ? "Schritt" : "Schritte"}</summary><ol>${m.steps.map((s) => `<li>${esc(s.label)}</li>`).join("")}</ol></details>`
        : "";
      const busy = m.status === "running" ? `<p class="cl-busy"><span class="spinner" aria-hidden="true"></span> ${esc(m.steps.at(-1)?.label ?? "Claude denkt nach")} …</p>` : "";
      const error = m.status === "error" ? `<p class="note warn">${esc(m.error ?? "Das hat nicht geklappt.")}</p>` : "";
      return `<div class="cl-a">${steps}${m.text ? `<div class="md">${markdown(m.text)}</div>` : ""}${busy}${error}</div>`;
    })
    .join("");
  const docs = c.documents.length
    ? `<div class="cl-docs"><p class="eyebrow">Dokumente in diesem Chat</p>${c.documents
        .map(
          (d) =>
            `<button type="button" class="cl-doc" data-link="dok:${esc(d.id)}"><b>${esc(d.title)}</b><small>${esc(fmtDT(d.createdAt))}${d.dealRef ? ` · am Deal ${esc(d.dealRef)}` : ""}</small></button>`,
        )
        .join("")}</div>`
    : "";
  return `<h2 class="cl-title">${esc(c.title)}</h2>${msgs}${docs}`;
}

export function renderClaude(): void {
  const root = $("#v-claude");
  if (!root.querySelector(".cl")) {
    root.innerHTML = `<div class="cl">
      <aside class="cl-side"><span class="eyebrow">Frag Claude</span><a class="btn primary" href="#claude">Neuer Chat</a><nav id="cl-list" aria-label="Chats"></nav></aside>
      <div class="cl-main"><div class="cl-msgs" id="cl-msgs" aria-live="polite"></div>
        <form class="cl-ask" id="cl-ask"><textarea name="q" rows="2" maxlength="8000" placeholder="Frag Claude zur Pipeline … (Umschalt+Enter für eine neue Zeile)" aria-label="Frage an Claude"></textarea>
          <div class="cl-ask-bar"><label class="sel"><input type="checkbox" name="web"> Auch im Web suchen</label><button class="btn primary" id="cl-send">Fragen</button></div></form></div></div>`;
  }
  $("#cl-list").innerHTML = chatList();
  const box = $("#cl-msgs");
  const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 80;
  box.innerHTML = conversation();
  if (atBottom) box.scrollTop = box.scrollHeight;
  const busy = running(S.claudeChat) || sending;
  $<HTMLButtonElement>("#cl-send").disabled = busy;
  $<HTMLButtonElement>("#cl-send").textContent = busy ? "Claude antwortet …" : "Fragen";
}

async function send(form: HTMLFormElement) {
  const q = form.elements.namedItem("q") as HTMLTextAreaElement;
  const web = (form.elements.namedItem("web") as HTMLInputElement).checked;
  const text = q.value.trim();
  if (text.length < 2 || sending || running(S.claudeChat)) return;
  sending = true;
  renderClaude();
  try {
    if (S.claudeChatId) await S.api.claudeAsk(S.claudeChatId, text, web);
    else {
      const id = await S.api.claudeStart(text, web);
      S.claudeChatId = id;
      history.replaceState(null, "", `#claude-${id}`);
    }
    q.value = "";
    (form.elements.namedItem("web") as HTMLInputElement).checked = false;
    await loadClaude();
    const box = $("#cl-msgs");
    renderClaude();
    box.scrollTop = box.scrollHeight;
  } catch (err) {
    toast(err instanceof Error ? err.message : "Die Frage ging nicht raus. Bitte noch einmal.");
  } finally {
    sending = false;
    renderClaude();
  }
}

export function bindClaude(): void {
  const root = $("#v-claude");
  root.addEventListener("submit", (e) => {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>("#cl-ask");
    if (!form) return;
    e.preventDefault();
    void send(form);
  });
  root.addEventListener("keydown", (e) => {
    const t = e.target as HTMLElement;
    if (t.matches("#cl-ask textarea") && e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      void send(t.closest("form")!);
    }
  });
  root.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const example = t.closest<HTMLElement>("[data-example]")?.dataset.example;
    if (example) {
      const q = $<HTMLTextAreaElement>("#cl-ask textarea");
      q.value = example;
      q.focus();
      return;
    }
    const el = t.closest<HTMLElement>("[data-link]");
    if (el?.dataset.link) void openPipelineLink(el.dataset.link, el.textContent ?? undefined);
  });
}

/** Called by the router for #claude and #claude-<id>. */
export function showClaude(chatId: string | null): void {
  if (S.claudeChatId !== chatId) {
    S.claudeChatId = chatId;
    S.claudeChat = null;
  }
  void loadClaude()
    .catch(() => toast("Frag Claude ließ sich nicht laden."))
    .then(() => render());
}
